import React, { useCallback, useState } from 'react';
import { useFlags } from '@fullstory/react-hooks';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { useNamedElementActions } from '@fullstory/redux';
import { NamedElementRecord } from '@fullstory/proto/fs/services/search-settings';
import {
  NamedElementModal,
  NamedElementModalSource,
  NamedElementModalVariant,
} from '../../components/NamedElementModal';
import { placeholderNamedElementRecord, isPendingSuggestion } from '../../utils';
import { CLOSED } from '../../constants';
import { SignalStudioPreviewResolvedEvent } from '@fullstory/signal-studio-context';
import { NamedElementWithPreview } from '../../types';

type NamedElementModalInput = {
  namedElement: NamedElementRecord;
  initialPreview?: SignalStudioPreviewResolvedEvent;
  exploreSuggestedElements?: NamedElementWithPreview[];
};
type ModalInput = NamedElementModalInput | typeof CLOSED;

interface NamedElementModalContainerProps {
  modalInput: ModalInput;
  setModalInput: (modalInput: ModalInput) => void;
  source: NamedElementModalSource;
  initialSpecSlugs?: string[];
  onClose?: (saved?: boolean) => void;
}

const NamedElementModalContainer: React.FC<NamedElementModalContainerProps> = ({
  modalInput,
  setModalInput,
  source,
  initialSpecSlugs,
  onClose,
}) => {
  const flags = useFlags();
  const restricted = !flags.hasBit(Flag.PermModifyNamedElements);
  const closeModal = (saved?: boolean) => {
    setModalInput(CLOSED);
    onClose?.(saved);
  };

  const {
    save: onSave,
    archive: onArchive,
    restore: onUnarchive,
    rejectSuggestion: onRejectSuggestion,
  } = useNamedElementActions();

  if (modalInput === CLOSED) {
    return null;
  }

  const variant = restricted
    ? NamedElementModalVariant.view
    : modalInput.namedElement?.namedElement?.id
    ? NamedElementModalVariant.edit
    : NamedElementModalVariant.new;

  return (
    <NamedElementModal
      data-component="NamedElementModal"
      id={
        modalInput.namedElement?.namedElement?.id ||
        modalInput.namedElement?.namedElement?.suggestion?.id
      }
      isSuggestion={isPendingSuggestion(modalInput.namedElement?.namedElement)}
      duplicate={false} // This component is never used for the duplicate case
      variant={variant}
      source={source}
      onSave={onSave}
      onArchive={onArchive}
      onUnarchive={onUnarchive}
      onCloseSignal={closeModal}
      initialElement={modalInput.namedElement?.namedElement || {}}
      initialSpecSlugs={initialSpecSlugs}
      onRejectSuggestion={onRejectSuggestion}
      initialPreview={modalInput.initialPreview}
      exploreSuggestedElements={modalInput.exploreSuggestedElements}
    />
  );
};

export const useNamedElementModal = (source: NamedElementModalSource) => {
  // State for the named element modal.
  // CLOSED    -> modal closed
  // undefined -> new named element
  // object    -> edit named element
  const [_modalInput, _setModalInput] = useState<ModalInput>(CLOSED);

  // keeping this named the same for compatibility with callers that just want to work with the named element directly
  const setModalInput = useCallback((namedElement: NamedElementRecord) => {
    _setModalInput({ namedElement });
  }, []);

  const namedElementProps = {
    modalInput: _modalInput,
    setModalInput: _setModalInput,
    source,
  };

  const onCreate = () => setModalInput(placeholderNamedElementRecord());

  const NamedElementModalComponent = <NamedElementModalContainer {...namedElementProps} />;
  const getNamedElementModalComponent = (
    overrideProps: Partial<React.ComponentProps<typeof NamedElementModalContainer>>,
  ) => <NamedElementModalContainer {...namedElementProps} {...overrideProps} />;

  return {
    setModalInput,
    setModalInputParams: _setModalInput,
    source,
    onCreate,
    NamedElementModalComponent,
    getNamedElementModalComponent,
  };
};
